import { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import SectionTitle from "../SectionTitle/SectionTitle";
import axios from "axios";
import { FaMapMarkerAlt, FaExternalLinkAlt } from "react-icons/fa";

const settings = {
  dots: false,
  arrows: true,
  speed: 1000,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  responsive: [
    { breakpoint: 1400, settings: { slidesToShow: 3 } },
    { breakpoint: 1200, settings: { slidesToShow: 2 } },
    { breakpoint: 991, settings: { slidesToShow: 2 } },
    { breakpoint: 767, settings: { slidesToShow: 1 } },
  ],
};

const stripHtmlTags = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, ''); // removes all HTML tags
};

const SisterCompany = () => {
  const [sisterCompany, setSisterCompany] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [maxHeight, setMaxHeight] = useState(0);
  const itemRefs = useRef([]);
  const apiUrl = process.env.REACT_APP_PUBLIC_API_URL;

  useEffect(() => {
    const getSisterCompany = async () => {
      try {
        const res = await axios.get(`${apiUrl}/api/sister-companies-list`);
        setSisterCompany(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getSisterCompany();
  }, [apiUrl]);

  useEffect(() => {
    if (sisterCompany.length > 0) {
      const heights = itemRefs.current.map((el) => el?.offsetHeight || 0);
      setMaxHeight(Math.max(...heights));
    }
  }, [sisterCompany]);

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  if (!sisterCompany) return null;

  return (
    <section 
      className="wpo-service-section-s2 section-padding"
      style={{
          backgroundColor: "#E6F7FF",
          paddingTop: "40px",   // reduce vertical padding
          paddingBottom: "40px" // reduce vertical padding
      }}
    >
      <div className="container">
        <SectionTitle subTitle={"Sister Companies"} Title={"All StarFamily Related Companies"} />
        <div className="row-grid wpo-service-slider-s2">
          <Slider {...settings}>
            {sisterCompany.map((sc, index) => {
              const isHovered = hoveredIndex === index;
              const isExpanded = expandedIndex === index;
              const description =
                stripHtmlTags(sc.description) ||
                "No description available";

              const shortDesc =
                description.length > 100
                  ? description.slice(0, 100) + "..."
                  : description;

              return (
                <div className="grid" key={index}>
                  <div
                    ref={(el) => (itemRefs.current[index] = el)}
                    style={{
                      minHeight: maxHeight || "auto",
                      background: "#fff",
                      borderRadius: "20px 20px 12px 12px",
                      overflow: "hidden",
                      transition: "all 0.4s ease",
                      // Blue gradient shadows - dark to light blue
                      boxShadow: isHovered
                        ? "0 20px 40px rgba(0, 82, 204, 0.25), 0 8px 25px rgba(0, 123, 255, 0.15), 0 5px 10px rgba(173, 216, 255, 0.1)"
                        : "0 8px 25px rgba(0, 82, 204, 0.15), 0 3px 10px rgba(0, 123, 255, 0.08), 0 2px 5px rgba(173, 216, 255, 0.05)",
                      transform: isHovered ? "translateY(-8px)" : "translateY(0)",
                      margin: "15px 10px",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                    }}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    
                    {/* Curved Top Section with Image */}
                    <div
                      style={{
                        position: "relative",
                        height: "180px",
                        overflow: "hidden",
                        borderBottom: "3px solid #f8f9fa",
                      }}
                    >
                      {/* Curved Bottom Edge for Image */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: "0",
                          left: "0",
                          width: "100%",
                          height: "30px",
                          background: "#fff",
                          borderRadius: "30px 30px 0 0",
                          zIndex: 2,
                        }}
                      />
                      
                      {/* Main Image */}
                      <img
                        src={`${apiUrl}/storage/` + sc.image}
                        alt={sc.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transform: isHovered ? "scale(1.1)" : "scale(1.05)",
                          transition: "transform 0.6s ease",
                          display: "block",
                          filter: isHovered ? "brightness(1.1)" : "brightness(1)",
                        }}
                      />
                      
                      {/* Gradient Overlay */}
                      <div
                        style={{
                          position: "absolute",
                          top: "0",
                          left: "0",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 70%)",
                          zIndex: 1,
                        }}
                      />
                    </div>

                    {/* Logo positioned in the curved space */}
                    <div
                      style={{
                        position: "absolute",
                        top: "130px", // Positioned in the curved area
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 10,
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        border: `4px solid ${isHovered ? "#007bff" : "#fff"}`,
                        backgroundColor: isHovered ? "#007bff" : "#fff",
                        // Enhanced blue shadow for logo
                        boxShadow: isHovered 
                          ? "0 8px 25px rgba(0, 123, 255, 0.4), 0 4px 15px rgba(173, 216, 255, 0.3)" 
                          : "0 6px 20px rgba(0, 82, 204, 0.25), 0 3px 10px rgba(0, 123, 255, 0.15)",
                        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={`${apiUrl}/storage/` + (sc.logo || sc.image)}
                        alt={`${sc.name} logo`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "50%",
                          transition: "all 0.3s ease",
                          transform: isHovered ? "scale(1.1)" : "scale(1)",
                          filter: isHovered ? "brightness(1.2)" : "brightness(1)",
                        }}
                      />
                    </div>

                    {/* Text Section with Hover Border */}
                    <div style={{ 
                      padding: "25px 20px 20px 20px",
                      textAlign: "center",
                      marginTop: "25px", // Space for the logo
                      position: "relative",
                      // Add border only to the content area (excluding image)
                      border: isHovered ? "2px solid #007bff" : "2px solid transparent",
                      borderTop: "none", // Remove top border to exclude image area
                      borderRadius: "0 0 12px 12px", // Match card bottom radius
                      margin: isHovered ? "-2px" : "0", // Compensate for border width
                      transition: "all 0.3s ease",
                      background: "#fff",
                    }}>
                      <h2
                        style={{
                          fontSize: "22px",
                          color: "#222",
                          fontWeight: 700,
                          marginBottom: "12px",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        {sc.name}
                      </h2>

                      <div
                        style={{
                          height: isExpanded ? "auto" : "60px",
                          overflow: "hidden",
                          marginBottom: "15px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "15px",
                            color: "#666",
                            lineHeight: "1.6",
                            textAlign: "left",
                          }}
                        >
                          {isExpanded ? description : shortDesc}
                        </p>
                      </div>

                      {description.length > 100 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(index);
                          }}
                          style={{
                              background: "transparent",
                              border: "none",
                              color: "#3b82f6",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: "pointer",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              transition: "all 0.2s ease",
                              marginBottom: "8px",
                          }}
                          onMouseEnter={(e) => {
                              e.target.style.background = "rgba(59, 130, 246, 0.1)";
                          }}
                          onMouseLeave={(e) => {
                              e.target.style.background = "transparent";
                          }}
                        >
                          {isExpanded ? "Show Less" : "Read More"}
                        </button>
                      )}

                      {/* Details Section */}
                      <div style={{ 
                        borderTop: "1px solid #f0f0f0",
                        paddingTop: "15px",
                        textAlign: "left",
                      }}>
                        {(sc.location || sc.address) && (
                          <p
                            style={{
                              fontSize: "14px",
                              color: "#555",
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "flex-start",
                            }}
                          >
                            <FaMapMarkerAlt
                              style={{
                                color: "#28a745",
                                minWidth: "16px",
                                marginRight: "8px",
                                marginTop: "2px",
                              }}
                            />
                            <span>
                              {sc.location ? (
                                <>
                                  <strong>{sc.location}</strong>
                                  {sc.address ? ` (${sc.address})` : null}
                                </>
                              ) : (
                                <>{sc.address}</>
                              )}
                            </span>
                          </p>
                        )}

                        {sc.size && (
                          <p
                            style={{
                              fontSize: "14px",
                              color: "#555",
                              marginBottom: "12px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ 
                              fontWeight: 600, 
                              marginRight: "8px",
                              color: "#333"
                            }}>
                              Size:
                            </span>
                            {sc.size}
                          </p>
                        )}

                        {(sc.map_url || sc.website_url) && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: "12px",
                              marginTop: "12px",
                              flexWrap: "wrap", // ensures good layout on small screens
                              textAlign: "center",
                            }}
                          >
                            {/* View on Map Button */}
                            {sc.map_url && (
                              <a
                                href={sc.map_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                  padding: "10px 20px",
                                  borderRadius: "8px",
                                  background: isHovered
                                    ? "linear-gradient(135deg, #0a2a6b, #3b82f6)"
                                    : "#f1f5f9",
                                  color: isHovered ? "#ffffff" : "#3b82f6",
                                  textDecoration: "none",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  transition: "all 0.3s ease",
                                  border: "1px solid #e2e8f0",
                                  boxShadow: isHovered
                                    ? "0 6px 18px rgba(59, 130, 246, 0.4)"
                                    : "0 2px 8px rgba(0, 0, 0, 0.05)",
                                  transform: isHovered ? "translateY(-3px)" : "translateY(0)",
                                }}
                              >
                                <FaExternalLinkAlt size={12} style={{ marginRight: "6px" }} />
                                Map
                              </a>
                            )}

                            {/* Visit Web Button */}
                            {sc.website_url && (
                              <a
                                href={sc.website_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                  padding: "10px 20px",
                                  borderRadius: "8px",
                                  background: isHovered
                                    ? "linear-gradient(135deg, #0a2a6b, #3b82f6)"
                                    : "#f1f5f9",
                                  color: isHovered ? "#ffffff" : "#3b82f6",
                                  textDecoration: "none",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  transition: "all 0.3s ease",
                                  border: "1px solid #e2e8f0",
                                  boxShadow: isHovered
                                    ? "0 6px 18px rgba(59, 130, 246, 0.4)"
                                    : "0 2px 8px rgba(0, 0, 0, 0.05)",
                                  transform: isHovered ? "translateY(-3px)" : "translateY(0)",
                                }}
                              >
                                <FaExternalLinkAlt size={12} style={{ marginRight: "6px" }} />
                                Web
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </section>
  );
};

export default SisterCompany;